// Sentry initialisation. Called once from `app/bootstrap/initSentry.ts`.
// No-ops when the DSN is empty (dev/CI) so feature code can keep using the
// SentryService surface without env-guards everywhere.

import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'
import { config, flags } from '@core/config'
import { getSessionId } from '@core/logging'

export const initSentry = (app: App, router: Router): void => {
  if (!flags.sentryEnabled) return

  Sentry.init({
    app,
    dsn: config.sentry.dsn,
    environment: config.sentry.environment,
    // `release` is Sentry's canonical name for service+version. Prefer the
    // explicit env override (typically a CI-injected git SHA) and fall back
    // to the build-time `<name>@<version>` from package.json.
    release: config.sentry.release || `${config.app.name}@${config.app.version}`,
    sendDefaultPii: false, // logger.redact strips PII before reaching Sentry
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: config.sentry.tracesSampleRate,
    // Keep trace headers on same-origin/dev-proxy traffic only. The dev API
    // CORS policy currently allows Authorization/Content-Type but not
    // Sentry's distributed tracing headers (`sentry-trace`, `baggage`), so
    // propagating them to the cross-origin API hosts makes browser preflight
    // fail before the request reaches the app.
    tracePropagationTargets: ['localhost'],
  })

  // session_id + service identity are set once at boot so every captured
  // event (and breadcrumb) carries them without callers having to thread
  // them through. Backend traces and Mixpanel events share the same ids —
  // see core/logging/identity.ts and config.app.
  Sentry.setTag('session_id', getSessionId())
  Sentry.setTag('service.name', config.app.name)
  Sentry.setTag('service.version', config.app.version)
}
